"use client";

import * as React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Folder,
  PanelLeft,
  Paperclip,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

import { Button } from "../primitives/button";
import { useI18n } from "../lib/i18n";
import { RadioGroup, RadioGroupItem } from "../primitives/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../primitives/tooltip";
import { useReducedMotion } from "../hooks";
import { cn } from "../lib/cn";

/**
 * SourceConfirmationCard — agentic "which file did you mean?" prompt.
 *
 * May 22, 2026. Workspace V2 surface (NOT inline chat). The agent
 * surfaces this card when a propose_* tool targets a file that only
 * appeared in the same turn's search results — the user disambiguates,
 * then the BE resumes the action and emits a normal ``action_proposal``.
 *
 * Design spec — see ``tasks/ui-ux-spec-source-confirmation-card.md``
 * (mirrored from the May 22 UX review):
 *
 *   * Chrome-stripe arancione laterale → signature "AI is asking".
 *   * 3 zones: header (query + topic) → orange-soft recap → candidate
 *     list (Radix RadioGroup, ↑/↓/Home/End built-in) → footer (Annulla/
 *     Conferma, Esc/Enter keyboard shortcuts).
 *   * Confidence chip ONLY for ``search_result`` candidates (sidebar/
 *     chat_attachment know they belong to the user — confidence would
 *     be noise).
 *   * Source attribution: icon + label, never colour-only (a11y +
 *     colour-blind safety). Same chip chrome for every source kind.
 *   * Enter/exit animations bound to existing tokens (``surface-in``
 *     360ms, 180ms ease-in exit). ``useReducedMotion`` respected.
 *   * Live region (``aria-live="polite"``) announces status changes
 *     to screen readers.
 *
 * Headless data flow: card is purely controlled — props carry the
 * payload + the three callbacks (select/confirm/cancel). Status state
 * machine + the actual REST POST/DELETE live in the consumer
 * (``SourceConfirmationSurface``). Keeps this component Storybookable
 * and unit-testable without mocking the network.
 */

export type SourceConfirmationStatus =
  | "pending"
  | "confirming"
  | "confirmed"
  | "error";

export interface SourceConfirmationCandidate {
  contentItemId: string;
  filename: string;
  source: "sidebar" | "chat_attachment" | "search_result";
  /**
   * Human storage-source label ("Google Drive" / "Caricato manualmente" / …)
   * for candidates resolved by name, where the meaningful distinction is WHERE
   * the file lives, not how it entered the turn. When present it's shown
   * verbatim in the source chip; otherwise the chip falls back to the
   * provenance label from {@link SOURCE_META}.
   */
  sourceLabel?: string | null;
  confidence: number | null;
  snippet: string | null;
}

export interface SourceConfirmationPendingActionSummary {
  /** Verb-noun phrase shown in the orange-soft recap. */
  description: string;
  /** Optional fork target filename — rendered as a mono chip. */
  newTitle: string | null;
  /** Edit count for the recap. */
  editsCount: number;
  /** What the LLM thought it was forking — shown as topic in header. */
  sourceDocumentTitle: string;
}

export interface SourceConfirmationCardProps {
  candidates: readonly SourceConfirmationCandidate[];
  selectedContentItemId: string;
  pendingAction: SourceConfirmationPendingActionSummary;
  status: SourceConfirmationStatus;
  errorMessage?: string | null;
  onSelect: (contentItemId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional class override; container retains shadcn surface tokens. */
  className?: string;
}

// ── Source-attribution map (icon + label + tooltip) ────────────────────────

const SOURCE_META: Record<
  SourceConfirmationCandidate["source"],
  {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    label: MessageDescriptor;
    tooltip: MessageDescriptor;
  }
> = {
  sidebar: {
    icon: PanelLeft,
    label: msg`Sidebar`,
    tooltip: msg`Aperto nella sidebar dei documenti`,
  },
  chat_attachment: {
    icon: Paperclip,
    label: msg`Chat`,
    tooltip: msg`Allegato in questa conversazione`,
  },
  search_result: {
    icon: Search,
    label: msg`Ricerca`,
    tooltip: msg`Trovato nella ricerca semantica`,
  },
};

/**
 * Fallback chip meta for any ``source`` value not in {@link SOURCE_META}.
 *
 * Defensive: the BE contract is the three-value provenance enum above, but a
 * payload carrying an unexpected ``source`` (e.g. a raw storage label leaking
 * from an upstream resolver) must NEVER white-screen the whole app — it
 * previously crashed on ``SOURCE_META[source].icon`` (June 18, 2026 incident).
 * With a real ``sourceLabel`` present this generic chip reads perfectly; without
 * one it degrades to a neutral "Sorgente" tag.
 */
const FALLBACK_SOURCE_META = {
  icon: Folder,
  label: msg`Sorgente`,
  tooltip: msg`Sorgente del documento`,
} as const;

// ── Confidence dot colour rule — see spec §6 ─────────────────────────────

function confidenceDotClass(confidence: number): string {
  if (confidence >= 0.75) return "bg-success";
  if (confidence >= 0.4) return "bg-ink-3";
  return "bg-warning";
}

// ── Inline subcomponents ──────────────────────────────────────────────────

function SourceChip({
  source,
  sourceLabel,
}: {
  source: SourceConfirmationCandidate["source"];
  sourceLabel?: string | null;
}) {
  const { i18n } = useI18n();
  // Resilient lookup — an unknown ``source`` must degrade, never throw.
  const meta = SOURCE_META[source] ?? FALLBACK_SOURCE_META;
  const Icon = meta.icon;
  // A concrete storage label ("Google Drive") wins over the generic provenance
  // label; the tooltip then carries the full label too for the truncated case.
  const label = sourceLabel?.trim() || i18n._(meta.label);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5",
            "bg-cream-deep font-medium text-[10px] text-ink-2 uppercase tracking-wide",
            "max-w-[160px] shrink-0",
          )}
        >
          <Icon size={11} className="shrink-0" />
          <span className="truncate">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        {sourceLabel?.trim() || i18n._(meta.tooltip)}
      </TooltipContent>
    </Tooltip>
  );
}

function ConfidenceChip({ confidence }: { confidence: number }) {
  const { i18n } = useI18n();
  const pct = Math.round(confidence * 100);
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-[10px] text-ink-3 tabular-nums"
      aria-label={i18n._(msg`Match ${pct} percento`)}
    >
      <span
        aria-hidden="true"
        className={cn("inline-block h-1.5 w-1.5 rounded-full", confidenceDotClass(confidence))}
      />
      {pct}% match
    </span>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden"
    >
      <div className="flex items-center justify-between gap-2 border-hairline border-t bg-background-error px-4 py-2 text-[12px] text-error-foreground">
        <span className="truncate">{message}</span>
        <button
          type="button"
          onClick={onRetry}
          className="font-medium text-[11px] underline underline-offset-2 hover:opacity-80"
        >
          <Trans>Riprova</Trans>
        </button>
      </div>
    </motion.div>
  );
}

// ── Live-region announcer ─────────────────────────────────────────────────

function useStatusAnnouncement(
  status: SourceConfirmationStatus,
  selectedFilename: string | null,
  errorMessage?: string | null,
): string {
  const { i18n } = useI18n();
  switch (status) {
    case "confirming":
      return selectedFilename
        ? i18n._(msg`Conferma in corso per ${selectedFilename}.`)
        : i18n._(msg`Conferma in corso.`);
    case "confirmed":
      return selectedFilename
        ? i18n._(msg`Documento confermato: ${selectedFilename}.`)
        : i18n._(msg`Documento confermato.`);
    case "error":
      return errorMessage
        ? i18n._(msg`Errore: ${errorMessage}`)
        : i18n._(msg`Errore durante la conferma.`);
    default:
      return "";
  }
}

// ── Main component ────────────────────────────────────────────────────────

export const SourceConfirmationCard = React.forwardRef<
  HTMLElement,
  SourceConfirmationCardProps
>(function SourceConfirmationCard(
  {
    candidates,
    selectedContentItemId,
    pendingAction,
    status,
    errorMessage,
    onSelect,
    onConfirm,
    onCancel,
    className,
  },
  ref,
) {
  const { i18n } = useI18n();
  const reduced = useReducedMotion();
  const titleId = React.useId();
  const liveRef = React.useRef<HTMLDivElement | null>(null);

  // Auto-focus the preferred radio on mount so keyboard users can
  // confirm immediately (Enter). Cf. spec §9 a11y.
  const radioGroupRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (status !== "pending") return;
    const root = radioGroupRef.current;
    if (!root) return;
    const target = root.querySelector<HTMLButtonElement>(
      `[data-state="checked"]`,
    );
    target?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // Esc closes; Enter confirms when the radio group has focus.
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (status !== "pending") return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === "Enter") {
        const target = e.target as HTMLElement | null;
        // Avoid hijacking Enter when focus is on a Button (it'll handle
        // its own activation). Confirm only when focus is inside the
        // radio list (Radix marks radios as role=radio).
        if (target?.getAttribute("role") === "radio") {
          e.preventDefault();
          onConfirm();
        }
      }
    },
    [status, onCancel, onConfirm],
  );

  const selected = candidates.find(
    (c) => c.contentItemId === selectedContentItemId,
  );
  const isPending = status === "pending";
  const isConfirming = status === "confirming";

  const motionProps = reduced
    ? {}
    : {
        initial: { opacity: 0, y: 8, scale: 0.99 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -4, scale: 0.99 },
        transition: { duration: 0.36, ease: [0.16, 1, 0.3, 1] as const },
      };

  const liveMessage = useStatusAnnouncement(
    status,
    selected?.filename ?? null,
    errorMessage,
  );

  return (
    <TooltipProvider delayDuration={400}>
      <motion.section
        ref={ref as React.RefObject<HTMLElement>}
        role="region"
        aria-labelledby={titleId}
        aria-busy={isConfirming}
        onKeyDown={handleKeyDown}
        {...motionProps}
        className={cn(
          "relative w-full max-w-[560px] rounded-lg border border-hairline bg-white shadow-md",
          "before:absolute before:top-0 before:left-0 before:h-full before:w-[3px]",
          "before:rounded-l-lg before:bg-ando-orange before:content-['']",
          isConfirming && "pointer-events-none opacity-95",
          className,
        )}
      >
        {/* Header */}
        <header className="flex items-start gap-2 px-4 py-3">
          <Sparkles
            size={14}
            className="mt-0.5 shrink-0 text-ando-orange"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <h3
              id={titleId}
              className="font-display font-medium text-[15px] text-ink-1 leading-tight"
            >
              <Trans>Quale file vuoi usare come base?</Trans>
            </h3>
            <p
              className="mt-0.5 truncate font-mono text-[12px] text-ink-2"
              title={pendingAction.sourceDocumentTitle}
            >
              {pendingAction.sourceDocumentTitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={!isPending}
            aria-label={i18n._(msg`Annulla (Esc)`)}
            className={cn(
              "shrink-0 rounded-sm p-1 text-ink-3 hover:bg-black/[0.04] hover:text-ink-1",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
              "transition-colors disabled:opacity-40 disabled:hover:bg-transparent",
            )}
          >
            <X size={14} />
          </button>
        </header>

        {/* Pending recap */}
        <div className="border-ando-orange/15 border-y bg-ando-orange-soft px-4 py-2.5">
          <p className="text-[13px] text-ink-2 leading-snug">
            <Trans>Sto per</Trans>{" "}
            <span className="font-medium text-ando-orange">
              {pendingAction.description}
            </span>
            <span className="text-ink-3"> · </span>
            <span className="font-mono text-[11px]">
              {pendingAction.editsCount}{" "}
              {pendingAction.editsCount === 1
                ? i18n._(msg`modifica`)
                : i18n._(msg`modifiche`)}
            </span>
            {pendingAction.newTitle ? (
              <>
                <span className="text-ink-3"> → </span>
                <span className="rounded-sm bg-cream-deep px-1.5 py-0.5 font-mono text-[11px] text-ink-1">
                  {pendingAction.newTitle}
                </span>
              </>
            ) : null}
          </p>
        </div>

        {/* Candidates */}
        <div ref={radioGroupRef} className="max-h-[280px] overflow-y-auto p-2">
          <RadioGroup
            value={selectedContentItemId}
            onValueChange={onSelect}
            disabled={!isPending}
            aria-label={i18n._(msg`Documenti candidati`)}
            className="gap-0.5"
          >
            {candidates.map((c) => {
              const isSelected = c.contentItemId === selectedContentItemId;
              const showConfidence =
                c.source === "search_result" && c.confidence !== null;
              const snippetId = `${titleId}-snippet-${c.contentItemId}`;
              return (
                <label
                  key={c.contentItemId}
                  htmlFor={`${titleId}-radio-${c.contentItemId}`}
                  className={cn(
                    "group flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-2 transition-colors",
                    "border",
                    isSelected
                      ? "border-ando-orange/30 bg-ando-orange-soft ring-1 ring-ando-orange/20"
                      : "border-transparent hover:bg-cream-deep",
                  )}
                >
                  <RadioGroupItem
                    value={c.contentItemId}
                    id={`${titleId}-radio-${c.contentItemId}`}
                    aria-describedby={snippetId}
                    className="mt-0.5"
                  />
                  <FileText
                    size={12}
                    className="mt-0.5 shrink-0 text-ink-3"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium text-[13px] text-ink-1">
                        {c.filename}
                      </span>
                      <SourceChip source={c.source} sourceLabel={c.sourceLabel} />
                      {showConfidence && c.confidence !== null ? (
                        <ConfidenceChip confidence={c.confidence} />
                      ) : null}
                    </div>
                    <p
                      id={snippetId}
                      className="mt-1 line-clamp-2 text-[12px] text-ink-2 leading-[1.45]"
                    >
                      {c.snippet ?? (
                        <span className="text-ink-3 italic">
                          <Trans>Nessuna preview disponibile</Trans>
                        </span>
                      )}
                    </p>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </div>

        {/* Error banner — conditional */}
        {status === "error" && errorMessage ? (
          <ErrorBanner message={errorMessage} onRetry={onConfirm} />
        ) : null}

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 border-hairline border-t px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={!isPending}
          >
            <Trans>Annulla</Trans>
            <kbd className="ml-1.5 rounded-sm border border-hairline px-1 py-px font-mono text-[9px] text-ink-3">
              Esc
            </kbd>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={!isPending || !selected}
          >
            {isConfirming ? (
              <>
                <span
                  className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden="true"
                />
                <Trans>Confermo…</Trans>
              </>
            ) : (
              <>
                <Trans>Conferma</Trans>
                <kbd className="ml-1.5 rounded-sm border border-white/30 px-1 py-px font-mono text-[9px] opacity-70">
                  ↵
                </kbd>
              </>
            )}
          </Button>
        </footer>

        {/* Screen-reader live announcer */}
        <div
          ref={liveRef}
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          {liveMessage}
        </div>
      </motion.section>
    </TooltipProvider>
  );
});
